import {
  Badge,
  FlexBox,
  IconButton,
  Skeleton,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { ampli } from 'lib-shared-tracking';
import { memo, useCallback, useMemo } from 'react';

import { AppLink } from 'components/appLink';
import { useServerStorage } from 'utils/useServerStorage';

import { useQueueDashboardQueueContext } from '../../queueDashboardQueue.context';

import styles from './queueDashboardQueueTitle.module.scss';

export const QueueDashboardQueueTitle = memo(
  function QueueDashboardQueueTitle(): JSX.Element {
    const { isSkeleton, ...context } = useQueueDashboardQueueContext();
    const queue = context.queue!;

    const storage = useServerStorage('user');

    const isFavorite = useMemo(
      (): boolean =>
        storage.data.dashboard?.favoriteQueueIds?.includes(queue.id) ?? false,
      [queue.id, storage.data.dashboard?.favoriteQueueIds],
    );

    const handleToggleFavorite = useCallback(() => {
      storage.set({
        dashboard: (draft) => {
          draft.favoriteQueueIds = draft.favoriteQueueIds ?? [];

          if (draft.favoriteQueueIds.includes(queue.id)) {
            draft.favoriteQueueIds = draft.favoriteQueueIds.filter(
              (id) => id !== queue.id,
            );

            ampli.dashboardQueueItemFavoriteToggled({ value: false });
          } else {
            draft.favoriteQueueIds.push(queue.id);
            ampli.dashboardQueueItemFavoriteToggled({ value: true });
          }
        },
      });
    }, [queue, storage]);

    return (
      <div className={styles.title}>
        {isSkeleton ? (
          <FlexBox
            justifyContent="center"
            alignItems="center"
            width="32px"
            height="32px"
          >
            <Skeleton height="18px" width="18px" />
          </FlexBox>
        ) : (
          <Tooltip
            content={
              isFavorite ? 'Remove from my favorites' : 'Add to my favorites'
            }
            disabled={isSkeleton}
          >
            <IconButton
              variant="tertiary"
              icon={isFavorite ? 'star-full' : 'star'}
              iconProps={{
                color: isFavorite ? colors.orange[300] : colors.neutral[400],
              }}
              onClick={handleToggleFavorite}
            />
          </Tooltip>
        )}

        {isSkeleton ? (
          <Skeleton height="20px" width="120px" />
        ) : (
          <>
            <AppLink
              path="/conversations"
              params={{
                search: {
                  queue: [queue.id],
                  status: ['open'],
                },
              }}
              onClick={() => {
                ampli.dashboardQueueNameClicked();
              }}
            >
              <Text.Heading variant="heading-3">{queue.name}</Text.Heading>
            </AppLink>

            {queue.is_default && (
              <Badge variant="primary" size="small">
                Default
              </Badge>
            )}

            {queue.is_archived && (
              <Badge variant="error" size="small">
                Archived
              </Badge>
            )}
          </>
        )}
      </div>
    );
  },
);
